import React, { useState } from "react";
import { ChevronRight, ChevronDown, Copy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const COLORS = {
  key: "#bb01a1",
  string: "#c41a16",
  number: "#1c00cf",
  boolean: "#aa0d91",
  null: "#8f8f8f",
  bracket: "#404040",
  comma: "#bcbcbc",
  rowHover: "#f6f6fc",
  copied: "#25ac3e",
};

function isPrimitive(value) {
  return (
    value === null || (typeof value !== "object" && typeof value !== "function")
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 800);
  }

  return (
    <span
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy"}
      style={{
        cursor: "pointer",
        marginLeft: 8,
        color: copied ? COLORS.copied : "#bbbbbb",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        transition: "color 0.18s",
      }}
    >
      <Copy size={15} />
    </span>
  );
}

function JSONValue({ value }) {
  if (value === null)
    return (
      <span style={{ color: COLORS.null, fontStyle: "italic" }}>null</span>
    );
  if (typeof value === "string")
    return <span style={{ color: COLORS.string }}>&quot;{value}&quot;</span>;
  if (typeof value === "number")
    return <span style={{ color: COLORS.number }}>{value}</span>;
  if (typeof value === "boolean")
    return (
      <span style={{ color: COLORS.boolean }}>{value ? "true" : "false"}</span>
    );
  return <span>{String(value)}</span>;
}

export default function CollapsibleJson({ data, level = 0 }) {
  const [collapsed, setCollapsed] = useState(level > 0);

  if (isPrimitive(data)) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 28,
          fontFamily: "Consolas, monospace",
          fontSize: 15,
        }}
      >
        <JSONValue value={data} />
        <CopyButton text={JSON.stringify(data)} />
      </span>
    );
  }

  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);

  return (
    <div
      style={{
        marginLeft: level * 22,
        fontFamily: "Consolas, monospace",
        fontSize: 15,
        userSelect: "text",
      }}
    >
      {/* Bracket Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: 28,
          cursor: "pointer",
          gap: 6,
          color: COLORS.bracket,
          fontWeight: 600,
          borderRadius: 6,
          transition: "background 0.15s",
          padding: "2px 6px 2px 0",
        }}
        tabIndex={0}
        role="button"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setCollapsed(!collapsed)
        }
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = COLORS.rowHover)
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "")}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </span>
        <span>
          {isArray ? "[" : "{"}
          {isArray && entries.length > 0 ? ` ${entries.length}` : ""}
        </span>
      </div>
      {/* Children */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              overflow: "hidden",
              paddingLeft: 22,
              borderLeft: "2px solid #f0f0f0",
            }}
            transition={{ duration: 0.22 }}
          >
            {entries.map(([key, value], i) => (
              <div
                key={isArray ? i : key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 28,
                  gap: 8,
                  background: "none",
                }}
              >
                {!isArray && (
                  <span
                    style={{
                      color: COLORS.key,
                      fontWeight: 500,
                      fontFamily: "inherit",
                      minWidth: 96,
                    }}
                  >
                    {key}
                  </span>
                )}
                {!isArray && <span style={{ color: COLORS.bracket }}>:</span>}
                <CollapsibleJson data={value} level={level + 1} />
                {i < entries.length - 1 && (
                  <span style={{ color: COLORS.comma, marginLeft: 5 }}>,</span>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      {/* Bracket Close Row */}
      <span
        style={{
          color: COLORS.bracket,
          fontWeight: 600,
          display: "block",
          minHeight: 28,
          marginLeft: 0,
        }}
      >
        {isArray ? "]" : "}"}
      </span>
    </div>
  );
}
